'use client';

import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import AppSwitcher from './AppSwitcher';
import SscLogo from '../../assets/images/logos/DataOpsLive_light_tagline.svg';
import { NavLink } from 'react-router-dom';
import UserProfile from '../common/UserProfile';
import DataOpsLivePopover from '../controls/DataOpsLivePopover';
import { type EventName, sendEvent } from '../../utilities/analytics';

function classNames(...classes: Array<string | undefined>) {
  return classes.filter(Boolean).join(' ');
}

export interface IVerticalNav {
  sections: IVerticalNavSection[];
}

export interface IVerticalNavSection {
  title?: string;
  items: IVerticalNavLink[];
}

export default function VerticalNav({ sections }: IVerticalNav) {
  return (
    <div className="flex grow flex-col gap-y-5 w-64 overflow-y-auto bg-gray-100 border-r border-dataops-taupe-gray/30">
      <div className="flex h-16 shrink-0 items-center pl-3 gap-4 bg-dataops-rich-black">
        <AppSwitcher />
        <a href="/">
          <div className="flex justify-center shrink-0 items-center">
            <img className="h-10 w-auto" src={SscLogo} alt="DataOps.live: Data Products Done Right" />
          </div>
        </a>
      </div>

      <nav className="flex flex-1 flex-col pt-6">
        <ul role="list" className="flex flex-1 flex-col px-3 pb-3 gap-y-10">
          {sections.map((section, idx) => (
            <li key={`section-${section.title ?? 'un-named'}-${idx}`}>
              <div className="text-sm font-normal uppercase pl-2 leading-6 text-gray-400">{section.title}</div>
              <ul role="list" className="mt-2 space-y-1">
                {section.items.map((item) => (
                  <li key={item.label}>
                    <VerticalNavLink item={item} />
                    {(item.tabs ?? []).length > 0 && item.label === 'Search' ? (
                      <>
                        {/* Solutions tabs */}
                        {(item.tabs ?? []).filter((tab) => tab.section === 'discover/solutions').length > 0 && (
                          <TabsWithHeader title="Solutions" sectionKey="discover/solutions" item={item} />
                        )}
                        {/* Hands on Labs tabs */}
                        {(item.tabs ?? []).filter((tab) => tab.section === 'discover/event-solutions').length > 0 && (
                          <TabsWithHeader title="Hands on Labs" sectionKey="discover/event-solutions" item={item} />
                        )}
                      </>
                    ) : (
                      (item.tabs ?? []).length > 0 && <Tabs item={item} />
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}

          <li className="mt-auto">
            <ul>
              <li>
                <UserProfile />
              </li>
              <li>
                <DataOpsLivePopover />
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </div>
  );
}

export interface IVerticalNavLink {
  label: string;
  href: string;
  icon: React.ElementType;
  onClickEventName: EventName;
  tabs?: IVerticalNavLinkTab[];
  isButtonStyle?: boolean;
}

function VerticalNavLink({ item }: { item: IVerticalNavLink }) {
  return (
    <NavLink
      to={item.href}
      className={({ isActive, isPending }) => {
        return classNames(
          item.isButtonStyle
            ? 'bg-dataops-yale-blue text-white font-normal hover:bg-dataops-yale-blue/80'
            : isActive
            ? 'bg-dataops-yale-blue/10 text-gray-700 font-normal hover:bg-dataops-yale-blue/20'
            : 'text-gray-700 hover:bg-dataops-platinum-gray hover:text-dataops-yale-blue',
          'group flex items-center gap-x-2 rounded-sm px-2 py-1.5 text-md font-normal leading-6',
        );
      }}
      data-testid={`nav-link-${item.label}`}
      onClick={() => {
        sendEvent({ name: item.onClickEventName });
      }}
      viewTransition
    >
      <item.icon
        aria-hidden="true"
        className={classNames(
          item.isButtonStyle ? 'text-white' : 'text-gray-400 group-hover:text-dataops-yale-blue',

          'h-5 w-5 shrink-0',
        )}
      />
      {item.label}
    </NavLink>
  );
}

export interface IVerticalNavLinkTab {
  section: 'discover/solutions' | 'discover/deployments' | 'discover/event-deployments' | 'discover/event-solutions';
  label: string;
  href: string;
  icon?: React.ElementType;
  closeCallback?: () => void;
  status?: 'loading' | 'loaded' | 'error';
}

function VerticalNavLinkTab({ tab }: { tab: IVerticalNavLinkTab }) {
  // Skip rendering tabs with no label
  if (!tab?.label) {
    return null;
  }

  return (
    <NavLink
      to={tab.href}
      className={({ isActive }) => {
        return classNames(
          isActive
            ? 'bg-dataops-yale-blue/10 text-gray-700 font-normal hover:bg-dataops-yale-blue/20'
            : 'text-gray-700 hover:bg-dataops-platinum-gray hover:text-dataops-yale-blue',
          tab.status === 'error' ? 'opacity-60' : '',
          'group flex items-center justify-between rounded-sm pl-2 pr-1 py-1 text-xs font-normal',
        );
      }}
      onClick={() => {
        sendEvent({
          name: 'navigation-tab-clicked',
          properties: { label: tab.label, section: tab.section, href: tab.href },
        });
      }}
      viewTransition
    >
      <div className="flex items-center gap-x-2 pl-[2px]">
        <div
          className={classNames(
            'flex-none h-4 w-4 rounded-md outline outline-1',
            tab.status === 'loading' ? 'animate-pulse bg-dataops-platinum-gray' : 'outline-dataops-process-cyan/20',
          )}
        >
          {tab.icon && <tab.icon className="text-dataops-process-cyan w-full h-full" aria-hidden="true" />}
        </div>
        <div className="w-40 text-ellipsis overflow-hidden whitespace-nowrap" title={tab.label}>
          {tab.label}
          {tab.status === 'loading' && <span className="ml-1 text-gray-400">(loading...)</span>}
          {tab.status === 'error' && <span className="ml-1 text-red-400">(error)</span>}
        </div>
      </div>
      <button
        className="relative truncate p-1 rounded-full hover:bg-gray-200"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          tab.closeCallback?.();
        }}
        title={`Close - ${tab.label}`}
      >
        <XMarkIcon className="text-gray-400 h-4 w-4" aria-hidden="true" />
      </button>
    </NavLink>
  );
}

interface TabsWithHeaderProps {
  title: string;
  sectionKey: string;
  item: IVerticalNavLink;
}

function TabsWithHeader({ title, sectionKey, item }: TabsWithHeaderProps) {
  return (
    <>
      <div
        className="text-gray-700 font-normal flex items-center justify-between rounded-md pl-2 pr-1 py-1 text-xs"
        aria-current="page"
      >
        <div className="flex flex-grow items-center justify-center gap-x-2 pl-[2px]">
          <div className="flex-none h-4 w-4"></div>
          <span className="text-gray-500">{title}</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        <div className="h-4 w-4 m-1"></div>
      </div>
      <ul role="list" className="pb-2 space-y-[1px]">
        {(item.tabs ?? [])
          .filter((tab) => tab.section === sectionKey)
          .map((tab) => (
            <VerticalNavLinkTab key={tab.label} tab={tab} />
          ))}
      </ul>
    </>
  );
}

interface TabsProps {
  item: IVerticalNavLink;
}

function Tabs({ item }: TabsProps) {
  return (
    <ul role="list" className="py-4 space-y-[1px]">
      {(item.tabs ?? []).map((tab) => (
        <VerticalNavLinkTab key={tab.label} tab={tab} />
      ))}
    </ul>
  );
}
